import { useState } from 'react'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Flex, Button, ColorVariant, colors } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Badge } from 'components/Badge/Badge'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { IMealRequestsTabProps } from 'types/foodLogistics/foodTabProps.interface'
import { IFoodMealRequest } from 'types/foodLogistics/foodMealRequest.interface'
import { FOOD_STATUS_COLORS } from '../foodData'
import { CreateMealRequestModal } from '../components/CreateMealRequestModal'
import { FoodDrawer, FoodDrawerOverlay } from '../Food.styled'
import { toast } from 'react-toastify'

export const MealRequestsTab = ({
  requests,
  setRequests,
  decideRequest,
  kitchens,
  windows,
  createOpen,
  setCreateOpen,
}: IMealRequestsTabProps) => {
  const [selected, setSelected] = useState<IFoodMealRequest | null>(null)

  const columns: IHonestTableColumn<IFoodMealRequest>[] = [
    {
      id: 'id',
      header: 'ID',
      sticky: true,
      minWidth: '7rem',
      cell: (r) => (
        <Cell>
          <BodyText weight='600'>{r.id}</BodyText>
        </Cell>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: (r) => (
        <Badge hexColor={FOOD_STATUS_COLORS[r.type]}>
          <BodyText>{r.type}</BodyText>
        </Badge>
      ),
    },
    {
      id: 'dayMeal',
      header: 'Day · Meal',
      cell: (r) => (
        <BodyText>
          {r.day} · {r.meal}
        </BodyText>
      ),
    },
    {
      id: 'dietary',
      header: 'Dietary Breakdown',
      minWidth: '12rem',
      cell: (r) => (
        <Flex wrap gap='0.25rem'>
          {r.lines.map((l, i) => (
            <Badge key={i} hexColor={colors.orange}>
              <BodyText>
                {l.tags.join('+')} ×{l.qty}
              </BodyText>
            </Badge>
          ))}
        </Flex>
      ),
    },
    { id: 'qty', header: 'Quantity', cell: (r) => <BodyText>{r.qty}</BodyText> },
    { id: 'routing', header: 'Routing', cell: (r) => <BodyText>{r.routing}</BodyText> },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge hexColor={FOOD_STATUS_COLORS[r.status]}>
          <BodyText>{r.status}</BodyText>
        </Badge>
      ),
    },
    { id: 'by', header: 'Submitted By', cell: (r) => <BodyText>{r.by}</BodyText> },
    {
      id: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <Button orangeBorder small onClick={() => setSelected(r)}>
          View
        </Button>
      ),
    },
  ]

  const handleSubmitNew = (request: IFoodMealRequest) => {
    setRequests((prev) => [...prev, request])
    toast.success(`${request.id} submitted — routing will be evaluated at approval time`)
    setCreateOpen(false)
  }

  return (
    <Flex column gap='1rem' width='100%'>
      <HonestTable columns={columns} data={requests} keyField='id' />

      {selected && (
        <>
          <FoodDrawerOverlay onClick={() => setSelected(null)} />
          <FoodDrawer>
            <Flex between width='100%' center>
              <TitleText weight='bold'>Request Details</TitleText>
              <CloseIcon onClick={() => setSelected(null)} />
            </Flex>
            <Flex gap='0.5rem' mt='16'>
              <Badge hexColor={FOOD_STATUS_COLORS[selected.type]}>
                <BodyText>{selected.type}</BodyText>
              </Badge>
              <Badge hexColor={FOOD_STATUS_COLORS[selected.status]}>
                <BodyText>{selected.status}</BodyText>
              </Badge>
            </Flex>
            {[
              ['REQUEST ID', selected.id],
              ['DAY · MEAL', selected.day + ' · ' + selected.meal],
              ['QUANTITY', String(selected.qty)],
              ['REASON', selected.reason],
              ['SUBMITTED BY', selected.by],
            ].map(([l, v]) => (
              <Flex key={l} column gap='0.25rem' mt='16'>
                <OtherText grey>{l}</OtherText>
                <BodyText>{v}</BodyText>
              </Flex>
            ))}
            <Flex column gap='0.5rem' mt='16'>
              <OtherText grey>DIETARY BREAKDOWN</OtherText>
              <Flex wrap gap='0.25rem'>
                {selected.lines.map((l, i) => (
                  <Badge key={i} hexColor={colors.orange}>
                    <BodyText>
                      {l.tags.join('+')} ×{l.qty}
                    </BodyText>
                  </Badge>
                ))}
              </Flex>
            </Flex>
            {selected.status === 'Pending' && (
              <>
                <Alert
                  mt='1rem'
                  title='Routing evaluated now: STANDBY'
                  description='Breakfast cut-off has passed. Diet-stock check: Regular (North ×6) · Halal  (North ×4). Hot orders are not increased.'
                  type={AlertType.WARNING}
                  colorVariant={ColorVariant.PRIMARY}
                />
                <Flex gap='0.5rem' mt='16'>
                  <Button onClick={() => { decideRequest(selected.id, false); setSelected(null) }}>
                    Reject
                  </Button>
                  <Button orange onClick={() => { decideRequest(selected.id, true); setSelected(null) }}>
                    Approve
                  </Button>
                </Flex>
              </>
            )}
          </FoodDrawer>
        </>
      )}

      {createOpen && (
        <CreateMealRequestModal
          kitchens={kitchens}
          windows={windows}
          existingCount={requests.length}
          closeModal={() => setCreateOpen(false)}
          onSubmit={handleSubmitNew}
        />
      )}
    </Flex>
  )
}
